/**
 * EmptyStateChip — the "collapsed" one-line row that replaces a full
 * card whenever its data is empty. Introduced 2026-02 during the
 * Manager Dashboard layout pass — full-card empty states were eating
 * ~140px each while communicating a single line of text.
 *
 * Layout: icon on the left, single-line message that truncates, tiny
 * chevron on the right. Whole row is a button so a tap opens the
 * relevant destination (Zone Sheet in most cases).
 *
 * Kept intentionally boring — no gradients or shadows. The chip should
 * *disappear* from the dashboard's visual weight, not compete with the
 * data-full cards above/below it. Cards that have actual data still
 * render their own full-fidelity presentation.
 */
import { ArrowRight } from "@phosphor-icons/react";

export default function EmptyStateChip({
  icon: Icon,
  tint = "slate",       // "slate" | "amber" | "rose" — sets the icon bg
  label,                // eyebrow text — "Game Plan", "Truck Plan", etc.
  message,              // body message — "No one clocked in yet"
  ctaLabel = "Zone Sheet",
  onClick,
  testid,
}) {
  const tintClasses = {
    slate: "bg-slate-100 text-slate-500",
    amber: "bg-amber-100 text-amber-700",
    rose: "bg-rose-100 text-rose-700",
  }[tint] || "bg-slate-100 text-slate-500";
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all text-left active:scale-[0.995]"
      data-testid={testid}
    >
      <span className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl ${tintClasses}`}>
        {Icon ? <Icon weight="fill" className="w-4 h-4" /> : null}
      </span>
      <div className="min-w-0 flex-1">
        {label && (
          <p className="text-[10px] uppercase tracking-[0.22em] font-black text-slate-400 leading-none">
            {label}
          </p>
        )}
        <p className="text-sm font-bold text-slate-700 leading-tight mt-0.5 truncate">
          {message}
        </p>
      </div>
      <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-blue-700">
        {ctaLabel}
        <ArrowRight weight="bold" className="w-3.5 h-3.5" />
      </span>
    </button>
  );
}
